import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, Loader2 } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartDrawer = () => {
  const { cartItems, removeFromCart, updateQuantity, cartTotal, clearCart } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        cart_items: cartItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        })),
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Une erreur est survenue lors du paiement');
    } finally {
      setIsLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]" data-testid="empty-cart">
        <ShoppingBag className="w-16 h-16 text-border mb-4" />
        <p className="text-text-secondary text-lg mb-2">Votre panier est vide</p>
        <p className="text-text-secondary text-sm mb-6">Découvrez nos produits d'exception</p>
        <Button
          onClick={() => navigate('/products')}
          className="btn-primary"
          data-testid="continue-shopping-btn"
        >
          Voir la boutique
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]" data-testid="cart-drawer">
      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {cartItems.map((item) => (
          <div
            key={item.product.id}
            className="flex gap-4 p-4 bg-surface rounded"
            data-testid={`cart-item-${item.product.id}`}
          >
            {/* Product Image */}
            <div className="w-20 h-24 flex-shrink-0 overflow-hidden">
              <img
                src={item.product.image}
                alt={item.product.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Product Info */}
            <div className="flex-1 min-w-0">
              <h4 className="font-serif text-text-primary truncate">
                {item.product.name}
              </h4>
              <p className="text-text-secondary text-sm mt-1">
                {item.product.fragrance}
              </p>
              <p className="text-accent font-medium mt-2">
                {item.product.price.toFixed(2)} €
              </p>

              {/* Quantity Controls */}
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                  className="quantity-btn"
                  data-testid={`decrease-qty-${item.product.id}`}
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center text-sm font-medium">
                  {item.quantity}
                </span>
                <button
                  onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                  className="quantity-btn"
                  data-testid={`increase-qty-${item.product.id}`}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Remove Button */}
            <button
              onClick={() => removeFromCart(item.product.id)}
              className="text-text-secondary hover:text-error transition-colors p-1"
              data-testid={`remove-item-${item.product.id}`}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>

      {/* Cart Footer */}
      <div className="border-t border-border pt-4 space-y-4">
        {/* Subtotal */}
        <div className="flex justify-between items-center">
          <span className="text-text-secondary">Sous-total</span>
          <span className="font-serif text-xl text-text-primary">
            {cartTotal.toFixed(2)} €
          </span>
        </div>

        {/* Shipping Note */}
        <p className="text-text-secondary text-sm text-center">
          Livraison calculée à l'étape suivante
        </p>

        {/* Checkout Button */}
        <Button
          onClick={handleCheckout}
          disabled={isLoading}
          className="w-full btn-primary flex items-center justify-center gap-2"
          data-testid="checkout-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Traitement...
            </>
          ) : (
            'Passer à la caisse'
          )}
        </Button>

        {/* Clear Cart */}
        <button
          onClick={clearCart}
          className="w-full text-text-secondary text-sm hover:text-error transition-colors"
          data-testid="clear-cart-btn"
        >
          Vider le panier
        </button>
      </div>
    </div>
  );
};

export default CartDrawer;
